import { calculateCheckDigit, extractReceiptNumber } from '../order-utils';
import { IOrder } from '../../models/purchase-history';
import { IOrderDetailsResponse } from '../../models/order-details';

/**
 * Pads a number string to 5 digits.
 */
export const padToFive = (value: string | number | undefined): string => {
  return value ? value.toString().padStart(5, '0') : '';
};

// Helper function to format receiptDetails to match receiptNumber
export const formatReceiptDetails = (details: string | undefined): string => {
  if (!details) return '';

  const [registerPart, transactionPart] = details.split('-'); // Split into parts
  const paddedRegisterNumber = registerPart ? padToFive(registerPart) : ''; // Pad register part
  const paddedTransactionId = transactionPart ? padToFive(transactionPart) : ''; // Pad transaction part

  return `${paddedRegisterNumber}-${paddedTransactionId}`; // Reassemble formatted string
};

/**
 * Builds the receipt number for B2B customers.
 */
export const buildB2BReceiptNumber = (
  registerNumber: string | number | undefined,
  transactionId: string | number | undefined,
  transactionIdWithCheckDigit?: string | number,
  customerType?: string,
  cpsDataSource?: string,
): string => {
  let transactionIdValue = transactionId?.toString() ?? '';
  if (customerType === 'b2b' && cpsDataSource === 'CPS') {
    transactionIdValue = transactionIdWithCheckDigit?.toString() ?? '';
  } else if (cpsDataSource === 'LSS' && transactionIdValue.length < 5) {
    transactionIdValue += calculateCheckDigit(
      registerNumber?.toString() || '',
      transactionIdValue.padStart(4, '0'),
    );
  }
  return `${padToFive(registerNumber)}-${padToFive(transactionIdValue)}`;
};

/**
 * Builds the receipt number for B2C customers.
 */
export const buildB2CReceiptNumber = (
  registerNumber: string | number | undefined,
  transactionId: string | number | undefined,
): string => {
  const requestedTransactionId =
    transactionId?.toString() +
      calculateCheckDigit(
        registerNumber?.toString() || '',
        transactionId?.toString().padStart(4, '0') || '',
      ) || '';
  return `${padToFive(registerNumber)}-${padToFive(requestedTransactionId)}`;
};

/**
 * Determines if the order matches by order number.
 */
const isOrderNumberMatch = (
  order: IOrderDetailsResponse,
  orderNumbers: string[] | undefined,
  orderNumber: string | undefined,
): boolean => {
  const orderNumberValue = order?.orderNumbers?.[0] || order.orderNumber;
  return (
    !!orderNumberValue &&
    orderNumberValue === (orderNumbers?.[0] || orderNumber)
  );
};

/**
 * Determines if the order matches by receipt number.
 */
const isReceiptNumberMatch = (
  order: IOrderDetailsResponse,
  requestedOrder: IOrder,
  customerType: string,
  cpsDataSource: string,
): boolean => {
  if (customerType === 'b2b') {
    const receiptNumber = buildB2BReceiptNumber(
      order.registerNumber,
      padToFive(order.transactionId),
      requestedOrder.transactionIdWithCheckDigit,
      customerType,
      cpsDataSource,
    );

    return (
      receiptNumber === formatReceiptDetails(requestedOrder.receiptDetails) &&
      order.grandTotalAmount === requestedOrder.totalAmount
    );
  }
  // b2c
  const b2cReceiptDetails = buildB2CReceiptNumber(
    requestedOrder.registerNumber,
    requestedOrder.transactionId,
  );
  const receiptNumber =
    extractReceiptNumber(order?.receiptImageSting || '') || '';
  return receiptNumber === b2cReceiptDetails;
};

/**
 * Retrieves the selected order detail from allOrderDetails.
 */
export const retrieveSelectedOrderDetail = (
  requestedOrder: IOrder,
  allOrderDetails: IOrderDetailsResponse[],
  customerType: string,
  cpsDataSource = 'LSS',
): IOrderDetailsResponse | null => {
  const { orderNumbers, orderNumber } = requestedOrder;

  return (
    allOrderDetails.find((order) => {
      if (order?.orderNumbers?.[0] || order.orderNumber) {
        return isOrderNumberMatch(order, orderNumbers, orderNumber);
      }

      return isReceiptNumberMatch(
        order,
        requestedOrder,
        customerType,
        cpsDataSource,
      );
    }) || null
  );
};
